import {
  decryptEncryptedJWT,
  getCookie,
  localStorageMethod
} from 'src/common/config/FunctionHelper';
import {
  tagManagerEvent,
  tagManagerPurchaseEvent,
  tagManagerEventWithSource,
  tagManagerLoginEvent
} from './TagManagerEventHelper';
import { posthogService } from './services/posthogService';
import { PostHogEvent } from './services/posthogEvents';

/**
 * This function is used to track when a new user signs up.
 *
 * @param {payload} email - The user's email address.
 */
export const signUpTrackEvent = (payload: any) => {
  let localIsFreeAudit = localStorageMethod('get', 'isFreeAudit');
  let sourceDetails: string | undefined;

  if (localIsFreeAudit == 'true') {
    tagManagerEvent('create-account-free-audit', payload.email);
    sourceDetails = 'pricing page';
  } else {
    if (localIsFreeAudit == 'true') {
      sourceDetails = 'pricing page';
    } else if (payload?.sourceDetail) {
      sourceDetails = 'free seo audit page';
    } else if (localIsFreeAudit == undefined && !payload?.sourceDetail) {
      sourceDetails = 'signup page';
    }
    let sourceData = { email: payload?.email, signup_source: sourceDetails };
    tagManagerEventWithSource('create-account', sourceData);
  }
  localStorageMethod('remove', 'isFreeAudit');

  // PostHog
  posthogService.track(PostHogEvent.AUTH_USER_SIGNED_UP, {
    email: payload?.email,
    signup_source: sourceDetails
  });
};

/**
 * This function is used to track when a user installs the plugin.
 */
export const pluginInstallTrackEvent = async () => {
  const encodedJWT = getCookie(process.env.REACT_APP_COOKIE_NAME);

  const jwtDecode: any = await decryptEncryptedJWT(encodedJWT);
  if (jwtDecode) {
    tagManagerEvent('download-plugin-click', jwtDecode?.email);
  }

  // PostHog
  posthogService.track(PostHogEvent.PLUGIN_INSTALL_CLICKED);
};

/**
 * This function is used to track when a user selects a free trial.
 *
 * @param {string} email - The user's email address.
 */
export const selectTrialTrackEvent = (email: string) => {
  tagManagerEvent('free-plan-click', email);

  // PostHog
  posthogService.track(PostHogEvent.BILLING_FREE_PLAN_SELECTED, { email });
};

/**
 * This function is used to track when a user selects a free trial plan.
 *
 * @param {string} email - The user's email address.
 */
export const selectTrialPlanTrackEvent = (email: string, planName: string) => {
  let sanitizePlanName =
    planName?.charAt(0)?.toUpperCase() + planName?.slice(1)?.toLowerCase();
  tagManagerEvent('free-trial-click-' + sanitizePlanName, email);

  // PostHog
  posthogService.track(PostHogEvent.BILLING_PLAN_SELECTED, { email, plan_name: planName });
};

/**
 * This function is used to track when a user completes a payment.
 *
 * @param {string} email - The user's email address.
 */
export const paymentTrackEvent = (email: string, ecommerce: any) => {
  tagManagerEvent('payment-successful', email);
  if (ecommerce && ecommerce.items) {
    ecommerce.items[0].plan_option = ecommerce?.items[0]?.item_category;
  }
  tagManagerPurchaseEvent('purchase', ecommerce);

  // PostHog
  posthogService.track(PostHogEvent.BILLING_PAYMENT_SUCCESSFUL, { email, ...ecommerce });
};

/**
 * This function is used to track when a user completes a payment.
 *
 * @param {string} email - The user's email address.
 */
export const auditCompleteTrackEvent = (site: string) => {
  tagManagerEvent('site-audit-complete', site);

  // PostHog
  posthogService.track(PostHogEvent.AUDIT_SITE_AUDIT_COMPLETED, { site_url: site });
};

export const beginCheckoutTrackEvent = (planName, payload: any) => {
  let sanitizePlanName =
    planName?.charAt(0)?.toUpperCase() + planName?.slice(1)?.toLowerCase();
  tagManagerPurchaseEvent('begin-checkout-' + sanitizePlanName, payload);

  // PostHog
  posthogService.track(PostHogEvent.BILLING_BEGIN_CHECKOUT, { plan_name: planName, ...payload });
};

export const loginGTMTrackEvent = (autoLogin: Boolean) => {
  tagManagerLoginEvent('login', autoLogin);

  // PostHog
  // posthogService.track(PostHogEvent.AUTH_USER_LOGGED_IN, { auto_login: autoLogin });
};
